import { type ApolloError, gql, useMutation } from "@apollo/client";
import { noop } from "lodash";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { QUICK_INVOICES_REVIEW_QUERY } from "@/apis/gql/apolloClient";
import { INVOICE_QUERY } from "./get";
import { useGetAllInvoices } from "./get-all";

type RemoveDuplicates = {
  invoiceIds: string[];
};

export const useRemoveDuplicates = (): [
  (
    data: RemoveDuplicates,
    onSuccess?: () => void,
    onError?: () => void,
  ) => Promise<string[]>,
  {
    data: any;
    loading: boolean;
    error?: ApolloError;
  },
] => {
  const { t } = useTranslation();
  const { fetchInvoices } = useGetAllInvoices({
    enabled: false,
    skipDbClear: true,
  });
  const [removeDuplicates, { data, loading, error }] = useMutation(
    REMOVE_DUPLICATES_MUTATION,
  );

  const handleRemoveDuplicates = async (
    data: RemoveDuplicates,
    onSuccess: () => void = noop,
    onError: () => void = noop,
  ) => {
    const toastId = toast.loading(t("generic_loading"));

    try {
      const response = await removeDuplicates({
        variables: { invoiceIds: data.invoiceIds },
        optimisticResponse: {
          removeInvoiceDuplicates: {
            __typename: "RemoveInvoiceDuplicatesPayload",
            invoices: data.invoiceIds.map((id) => ({
              __typename: "Invoice",
              invoiceId: id,
            })),
          },
        },
        update: (cache, { data: { responseData } }) => {
          const quickReviewData: any = cache.readQuery({
            query: QUICK_INVOICES_REVIEW_QUERY,
          });

          if (quickReviewData?.invoiceQuickReview) {
            const updatedInvoices = quickReviewData.invoiceQuickReview.map(
              (invoice: any) => {
                if (data.invoiceIds.includes(invoice.invoiceId)) {
                  return {
                    ...invoice,
                    duplicates: [],
                    isDuplicate: false,
                    invoice: {
                      ...invoice.invoice,
                      isDuplicate: false,
                    },
                  };
                }
                return invoice;
              },
            );

            cache.writeQuery({
              query: QUICK_INVOICES_REVIEW_QUERY,
              data: {
                invoiceQuickReview: updatedInvoices,
              },
            });
          }

          data.invoiceIds.forEach((invoiceId) => {
            try {
              const invoiceData: any = cache.readQuery({
                query: INVOICE_QUERY,
                variables: { invoiceId },
              });

              if (invoiceData?.invoiceDetails) {
                cache.writeQuery({
                  query: INVOICE_QUERY,
                  variables: { invoiceId },
                  data: {
                    invoiceDetails: {
                      ...invoiceData.invoiceDetails,
                      duplicates: [],
                      invoice: {
                        ...invoiceData.invoiceDetails.invoice,
                        isDuplicate: false,
                      },
                    },
                  },
                });
              }
            } catch (error) {
              console.log(`Invoice ${invoiceId} not found in cache`);
            }
          });
        },
      });

      if (response.errors) {
        throw new Error("error creating line item");
      }

      onSuccess();

      await fetchInvoices({
        query: {
          invoiceId: {
            value: data.invoiceIds,
            op: "in",
          },
        },
      });

      toast.success(t("generic_success"), { id: toastId });
      return response.data?.removeInvoiceDuplicates.invoices;
    } catch (error) {
      onError();
      toast.error(t("generic_error"), { id: toastId, richColors: true });
      throw error;
    }
  };

  return [handleRemoveDuplicates, { data, loading, error }];
};

export const REMOVE_DUPLICATES_MUTATION = gql`
  mutation RemoveInvoiceDuplicates($invoiceIds: [String!]!) {
    removeInvoiceDuplicates(invoiceIds: $invoiceIds) {
      invoices {
        invoiceDetailsId
        invoice {
          invoiceId
        }
      }
    }
  }
`;
